import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	IconButton,
	Paper,
	Skeleton,
	Tooltip,
	Typography,
} from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import {
	ArrowBack,
	CheckCircle,
	TableChart,
	PictureAsPdf,
	Comment,
	Edit,
	AddAlert,
	NotificationsOff,
	ContentPasteOff,
	MoneyOff,
	CopyAll,
	CopyAllOutlined,
	ContentCopy,
	ReceiptLong,
} from '@mui/icons-material';
import {
	canHavePaymentReceipt,
	downloadPaymentReceipt,
	usePaymentReceipts,
} from '../../../utils/paymentReceipt';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	downloadInvoicePdfRequest,
	downloadInvoiceUdrCsvRequest,
	encryptInvoiceNumberRequest,
	sendEmailForInvoiceRequest,
	updateInvoiceGpsrRequest,
	nullifyInvoiceRequest,
	removeInvoiceLpfRequest,
} from 'redux/reducers/invoiceManagement/reducer';
import { PATHS } from 'routes/constants';
import { IS_NEMAN } from 'services/constants/env';
import { EasTableValue } from 'ui/EasTable/EasTableRow/EasTableValue';
import { parseInvoiceDurationToPeriodString, tableStructure } from '../tableStructure';
import { TInvoiceDetailProps } from './types';
import { InvoicePaymentDialog } from '../InvoicePaymentDialog';
import { InvoiceCommentDialog } from '../InvoiceCommentDialog';
import { ConfirmationDialogWithComment } from '../ConfirmationDialogWithComment';
import { COLORS } from '../../../services/constants/colors';
import { ETableStructureAlign, ETableStructureType } from '../../../ui/EasTable/types';
import { CountryWithFlag } from '../../../ui/CountryWithFlag';
import {
	selectDownloadInvoicePdfLoader,
	selectDownloadInvoiceUdrCsvLoader,
	selectEncryptedInvoiceNumber,
} from '../../../redux/reducers/invoiceManagement/selectors';
import { ConfirmationDialog } from '../../../ui/ConfirmationDialog';
import { TInvoiceListItemWithId } from './types';
import { RenderField } from './RenderField';
import { getEmPaymentMethodsRequest } from '../../../redux/reducers/emManagement/reducer';
import { getEmPaymentMethodsData } from '../../../redux/reducers/emManagement/selectors';
import { getSavedUrl } from '../../../redux/reducers/auth/selectors';

export const InvoiceDetail: FC<TInvoiceDetailProps> = ({ invoice, loading }) => {
	const dispatch = useDispatch();
	const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
	const [openCommentDialog, setOpenCommentDialog] = useState(false);
	const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
	const [openSuspendNotificationDialog, setOpenSuspendNotificationDialog] = useState(false);
	const [openNullifyInvoiceDialog, setOpenNullifyInvoiceDialog] = useState(false);
	const [openRemoveLpfDialog, setOpenRemoveLpfDialog] = useState(false);
	const [nullifyInvoiceComment, setNullifyInvoiceComment] = useState('');
	const [removeLpfComment, setRemoveLpfComment] = useState('');
	const [isCopyingPaymentLink, setIsCopyingPaymentLink] = useState(false);
	const downloadInvoicePdfLoader = useSelector(selectDownloadInvoicePdfLoader);
	const downloadInvoiceUdrCsvLoader = useSelector(selectDownloadInvoiceUdrCsvLoader);
	const encryptedInvoiceNumber = useSelector(selectEncryptedInvoiceNumber);
	const emPaymentMethods = useSelector(getEmPaymentMethodsData);
	const savedUrl = useSelector(getSavedUrl);
	const navigate = useNavigate();
	const itemWithId: TInvoiceListItemWithId | null = useMemo(() => {
		if (!invoice) return null;
		return { ...invoice, id: invoice.invoice_id };
	}, [invoice]);

	// receipt of the online payment (only invoices paid through the payment gateway have one)
	const receiptInvoiceIds = useMemo(
		() => (invoice && canHavePaymentReceipt(invoice.payment_status) ? [invoice.invoice_id] : []),
		[invoice],
	);
	const paymentReceiptUrl = usePaymentReceipts(receiptInvoiceIds).get(invoice?.invoice_id ?? 0);

	const detailRequestPayload = useCallback(
		() =>
			invoice
				? {
						tableName: 'invoice_list_info',
						orderBy: ['timestamp desc'],
						filter: [
							{
								name: 'invoice_number',
								value: String(invoice.invoice_number),
								comparator: '=' as const,
							},
						],
						page: { size: 1, from: 0 },
					}
				: null,
		[invoice],
	);

	const handleCopyPaymentLink = useCallback(() => {
		if (!encryptedInvoiceNumber?.paymentLink) return;
		navigator.clipboard.writeText(encryptedInvoiceNumber.paymentLink);
		setIsCopyingPaymentLink(true);
		setTimeout(() => {
			setIsCopyingPaymentLink(false);
		}, 2000);
	}, [encryptedInvoiceNumber]);

	const handleAction = useCallback(
		(typeOfDialog: string) => {
			if (!itemWithId) return;
			if (typeOfDialog === 'Download invoice in PDF') {
				dispatch(
					downloadInvoicePdfRequest({
						emId: itemWithId.em_id,
						invoiceId: itemWithId.invoice_id,
					}),
				);
			} else if (typeOfDialog === 'Download invoice in CSV') {
				dispatch(
					downloadInvoiceUdrCsvRequest({
						emId: itemWithId.em_id,
						invoiceId: itemWithId.invoice_id,
					}),
				);
			} else if (typeOfDialog === 'Mark as paid or edit payment information') {
				setOpenPaymentDialog(true);
			} else if (typeOfDialog === 'Add or edit comments for the invoice') {
				setOpenCommentDialog(true);
			}
		},
		[dispatch, itemWithId],
	);

	const handleNotificationConfirm = () => {
		if (!itemWithId) return;
		dispatch(
			sendEmailForInvoiceRequest({
				invoiceId: itemWithId.invoice_id,
				body: {
					invoice_id: itemWithId.invoice_id,
					invoice_number: itemWithId.invoice_number,
					timestamp: new Date().toISOString(),
					client_number: itemWithId.client_number ?? '',
					due_date: itemWithId.due_date ?? '',
					reference_number: itemWithId.reference_number,
					payment_status: itemWithId.payment_status ?? '',
					communication_status: itemWithId.communication_status ?? '',
					eur_amount: itemWithId.invoiced_amount_in_eur ?? 0,
					paid_amount: itemWithId.paid_amount ?? 0,
				},
			}),
		);
		setOpenNotificationDialog(false);
	};

	const handleSuspendNotificationConfirm = () => {
		if (!itemWithId) return;
		dispatch(
			updateInvoiceGpsrRequest({
				body: {
					tableName: 'invoice_gpsr',
					data: {
						communication_status: 'suspended',
						id: itemWithId.invoice_id,
					},
				},
			}),
		);
		setOpenSuspendNotificationDialog(false);
	};

	const handleNullifyInvoiceConfirm = useCallback(
		(comment: string) => {
			if (!itemWithId) return;
			const payload = detailRequestPayload();
			if (!payload) return;

			dispatch(
				nullifyInvoiceRequest({
					body: {
						invoiceId: itemWithId.invoice_id,
						comment,
					},
					invoiceListRequest: payload,
				}),
			);
			setNullifyInvoiceComment('');
			setOpenNullifyInvoiceDialog(false);
		},
		[dispatch, detailRequestPayload, itemWithId],
	);

	const handleRemoveLpfConfirm = useCallback(
		(comment: string) => {
			if (!itemWithId) return;
			const payload = detailRequestPayload();
			if (!payload) return;

			dispatch(
				removeInvoiceLpfRequest({
					body: {
						invoiceId: itemWithId.invoice_id,
						comment,
					},
					invoiceListRequest: payload,
				}),
			);
			setRemoveLpfComment('');
			setOpenRemoveLpfDialog(false);
		},
		[dispatch, detailRequestPayload, itemWithId],
	);

	useEffect(() => {
		if (!itemWithId) return;
		// Neman is not served by the payment gateway: a Neman invoice number encrypted with the EAS key
		// would give a link to someone else's invoice.
		if (!IS_NEMAN) {
			dispatch(encryptInvoiceNumberRequest({ invoiceNumber: itemWithId.invoice_number }));
		}
		dispatch(getEmPaymentMethodsRequest({ emId: itemWithId.em_id }));
	}, [dispatch, itemWithId]);

	const handleReplayHistory = () => {
		if (savedUrl && savedUrl !== window.location.pathname) {
			if (savedUrl.includes(PATHS.INVOICE_MANAGEMENT_PAGE)) navigate(savedUrl);
		} else navigate(PATHS.INVOICE_MANAGEMENT_PAGE);
	};

	if (loading) {
		return (
			<Box sx={{ p: 2 }}>
				<Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
				<Grid container spacing={2}>
					{Array.from({ length: 12 }).map((_, i) => (
						<Grid item xs={12} sm={6} md={4} key={i}>
							<Skeleton variant="text" width="100%" height={32} />
						</Grid>
					))}
				</Grid>
			</Box>
		);
	}

	if (!invoice || !itemWithId) {
		return (
			<Box sx={{ p: 2 }}>
				<Button
					component={Link}
					to={PATHS.INVOICE_MANAGEMENT_PAGE}
					startIcon={<ArrowBack />}
					sx={{ mb: 2 }}
				>
					Back to list
				</Button>
				<Typography color="text.secondary">Invoice not found.</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			<Box sx={{ maxWidth: '1000px', width: '100%' }}>
				<Paper
					sx={{
						p: 0,
						mt: 1,
						width: '100%',
						borderRadius: '0 0 12px 12px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							width: '100%',
							flexWrap: 'wrap',
							backgroundColor: COLORS.BACKGROUND_SECONDARY,
							p: '0.5rem 1rem 0.5rem 1rem',
							borderRadius: '12px 12px 0 0',
						}}
					>
						<Button startIcon={<ArrowBack />} onClick={handleReplayHistory}>
							<Typography variant="body1" color="primary" fontWeight="bold">
								Invoice Management
							</Typography>
						</Button>
						<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
							<EasTooltip title="Download invoice in PDF">
								<Box sx={{ position: 'relative' }}>
									<IconButton
										size="small"
										onClick={() => handleAction('Download invoice in PDF')}
										disabled={downloadInvoicePdfLoader}
									>
										<PictureAsPdf
											sx={{
												fontSize: '1.5rem',
												color: COLORS.PRIMARY,
												opacity: downloadInvoicePdfLoader ? 0.25 : 1,
											}}
										/>
									</IconButton>
									{downloadInvoicePdfLoader && (
										<CircularProgress
											size={16}
											color="primary"
											sx={{ position: 'absolute', right: 6, top: 6, zIndex: 1000 }}
										/>
									)}
								</Box>
							</EasTooltip>
							<EasTooltip title="Download Invoice appendix in CSV">
								<Box sx={{ position: 'relative' }}>
									<IconButton
										size="small"
										onClick={() => handleAction('Download invoice in CSV')}
										disabled={downloadInvoiceUdrCsvLoader}
									>
										<TableChart
											sx={{
												fontSize: '1.5rem',
												color: COLORS.PRIMARY,
												opacity: downloadInvoiceUdrCsvLoader ? 0.25 : 1,
											}}
										/>
									</IconButton>
									{downloadInvoiceUdrCsvLoader && (
										<CircularProgress
											size={16}
											color="primary"
											sx={{ position: 'absolute', right: 6, top: 6, zIndex: 1000 }}
										/>
									)}
								</Box>
							</EasTooltip>
							{paymentReceiptUrl && (
								<EasTooltip title="Download payment receipt">
									<IconButton
										size="small"
										onClick={() => downloadPaymentReceipt(paymentReceiptUrl)}
									>
										<ReceiptLong sx={{ fontSize: '1.5rem', color: COLORS.PRIMARY }} />
									</IconButton>
								</EasTooltip>
							)}
							<EasTooltip title="Send notification to client">
								<IconButton size="small" onClick={() => setOpenNotificationDialog(true)}>
									<AddAlert sx={{ fontSize: '1.5rem', color: COLORS.PRIMARY }} />
								</IconButton>
							</EasTooltip>
							<EasTooltip title="Suspend notifications on invoice payments">
								<IconButton size="small" onClick={() => setOpenSuspendNotificationDialog(true)}>
									<NotificationsOff sx={{ fontSize: '1.5rem', color: COLORS.PRIMARY }} />
								</IconButton>
							</EasTooltip>
							<EasTooltip title="Nullify invoice">
								<IconButton size="small" onClick={() => setOpenNullifyInvoiceDialog(true)}>
									<ContentPasteOff sx={{ fontSize: '1.5rem', color: COLORS.PRIMARY }} />
								</IconButton>
							</EasTooltip>

							<EasTooltip title="Remove LPF">
								<IconButton size="small" onClick={() => setOpenRemoveLpfDialog(true)}>
									<MoneyOff sx={{ fontSize: '1.5rem', color: COLORS.PRIMARY }} />
								</IconButton>
							</EasTooltip>
							<EasTooltip title={`${itemWithId.comment ? 'Edit' : 'Add'} comments for the invoice`}>
								<IconButton
									size="small"
									onClick={() => handleAction('Add or edit comments for the invoice')}
								>
									<Comment sx={{ fontSize: '1.5rem', color: COLORS.PRIMARY }} />
								</IconButton>
							</EasTooltip>

							<EasTooltip title="Mark as paid or edit payment information">
								<IconButton
									size="small"
									onClick={() => handleAction('Mark as paid or edit payment information')}
								>
									<CheckCircle
										sx={{
											fontSize: '1.5rem',
											color:
												itemWithId.payment_status === 'paid'
													? 'success.main'
													: itemWithId.payment_status === 'partially_paid'
														? 'warning.main'
														: itemWithId.payment_status === 'pending'
															? 'warning.main'
															: itemWithId.payment_status === 'overdue'
																? 'error.main'
																: 'primary.main',
										}}
									/>
								</IconButton>
							</EasTooltip>
							{/* <IconButton
							size="small"
							onClick={() => handleAction('Add or edit comments for the invoice')}
						>
							<Comment
								sx={{
									fontSize: '1.5rem',
									color: itemWithId.comment ? COLORS.PRIMARY : COLORS.BACKGROUND_DARK,
								}}
							/>
						</IconButton> */}
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							width: '100%',
							p: '2rem 2rem 2rem 4rem',
						}}
					>
						{/* Header row with main info and actions */}
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: 2,
								flexWrap: 'wrap',
								width: '100%',
							}}
						>
							<Grid container spacing={2}>
								<Grid item md={6} />
								<Grid item md={6}>
									<Typography
										variant="h1"
										component="h1"
										sx={{ color: COLORS.PRIMARY, fontSize: '2.5rem', fontWeight: 'bold' }}
									>
										INVOICE
									</Typography>
								</Grid>
							</Grid>
							<Grid container spacing={2}>
								<Grid item md={6}>
									<Box>
										<Typography
											variant="h5"
											component="h2"
											sx={{ color: COLORS.PRIMARY, fontWeight: 'bold', mb: 1 }}
										>
											Billing to
										</Typography>
									</Box>
									<Typography
										variant="body1"
										component="p"
										sx={{ color: COLORS.font_main, fontWeight: 'bold', fontSize: '1.6rem' }}
									>
										<EasTableValue
											type={ETableStructureType.EM}
											value={{ id: itemWithId.em_id, name: itemWithId.client }}
											item={itemWithId}
											size="xl"
											bold
											// color={COLORS.PRIMARY}
										/>
									</Typography>
									<Box sx={{ display: 'block', mt: 1 }}>
										<EasTableValue
											type={ETableStructureType.COUNTRY_NAME}
											value={itemWithId.country}
											item={itemWithId}
											size="l"
											bold
										/>
									</Box>
									<Box sx={{ mt: 1 }}>
										<EasTableValue
											type={ETableStructureType.EMAIL}
											value={itemWithId.email}
											item={itemWithId}
											size="l"
											bold
										/>
									</Box>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
										<Typography
											variant="body1"
											component="p"
											sx={{ color: COLORS.PRIMARY, fontSize: '1rem' }}
										>
											Client ID:
										</Typography>
										<Typography
											variant="body1"
											component="p"
											sx={{ color: COLORS.font_main, fontWeight: 'bold', fontSize: '1.0rem' }}
										>
											{itemWithId.client_number}
										</Typography>
									</Box>

									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
										{emPaymentMethods && emPaymentMethods.length > 0 ? (
											<Box>
												<Typography
													variant="body1"
													component="p"
													sx={{
														color: COLORS.font_main,
														fontWeight: 'bold',
														fontSize: '1.0rem',
														mt: 1,
													}}
												>
													<span style={{ fontWeight: 'normal', color: COLORS.PRIMARY }}>
														Payment method:
													</span>{' '}
													{emPaymentMethods[0].type} {emPaymentMethods[0].issuer}
												</Typography>
											</Box>
										) : (
											<Box>
												<Typography
													variant="body1"
													component="p"
													sx={{
														color: COLORS.font_main,
														fontWeight: 'bold',
														fontSize: '1.0rem',
														mt: 1,
													}}
												>
													<span style={{ fontWeight: 'normal', color: COLORS.PRIMARY }}>
														Payment method:
													</span>{' '}
													Not configured
												</Typography>
											</Box>
										)}
									</Box>
								</Grid>
								<Grid item md={6}>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
										{!IS_NEMAN && encryptedInvoiceNumber?.paymentLink && (
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
												{/* <LinkOutlined
													sx={{ fontSize: '1.2rem', color: COLORS.PRIMARY, cursor: 'pointer' }}
												/> */}
												<Link
													to={encryptedInvoiceNumber?.paymentLink ?? ''}
													target="_blank"
													rel="noopener noreferrer"
													style={{
														color: COLORS.PRIMARY,
														fontSize: '1rem',
														fontWeight: 'bold',
														maxWidth: '410px',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
												>
													{encryptedInvoiceNumber?.paymentLink}
												</Link>
												<Box sx={{ position: 'relative' }}>
													<ContentCopy
														sx={{
															fontSize: '1.0rem',
															color: COLORS.PRIMARY,
															cursor: 'pointer',
															opacity: isCopyingPaymentLink ? 0.25 : 1,
														}}
														onClick={handleCopyPaymentLink}
													/>
													{isCopyingPaymentLink && (
														<CircularProgress
															size={14}
															color="primary"
															sx={{ position: 'absolute', right: 0, top: 0, zIndex: 1000 }}
														/>
													)}
												</Box>
											</Box>
										)}
										{/* Top grid with main columns */}
										<RenderField
											item={itemWithId}
											selector="invoice_number"
											label="Invoice number"
											gridColumns={[7, 5]}
											align={ETableStructureAlign.RIGHT}
											type={ETableStructureType.COPYABLE_TEXT}
											valueBold
										/>
										<RenderField
											item={itemWithId}
											selector="invoice_date"
											label="Invoice date"
											gridColumns={[7, 5]}
											align={ETableStructureAlign.RIGHT}
										/>
										<RenderField
											item={itemWithId}
											selector="due_date"
											label="Due date"
											gridColumns={[7, 5]}
											align={ETableStructureAlign.RIGHT}
										/>
										<RenderField
											item={itemWithId}
											selector="invoice_duration"
											label="Invoice duration"
											gridColumns={[6, 6]}
											align={ETableStructureAlign.RIGHT}
											value={
												itemWithId.invoice_duration
													? parseInvoiceDurationToPeriodString(itemWithId.invoice_duration)
													: null
											}
										/>

										<RenderField
											item={itemWithId}
											selector="service_type"
											label="Service type"
											gridColumns={[7, 5]}
											align={ETableStructureAlign.RIGHT}
										/>
										<RenderField
											item={itemWithId}
											selector="communication_status"
											label="Communication status"
											gridColumns={[7, 5]}
											align={ETableStructureAlign.RIGHT}
										/>
										<RenderField
											item={itemWithId}
											selector="payment_status"
											label="Payment status"
											gridColumns={[7, 5]}
											align={ETableStructureAlign.RIGHT}
										/>
										<RenderField
											item={itemWithId}
											selector="legal_status_with_reg_country"
											label="Legal status with registered country"
											gridColumns={[7, 5]}
											align={ETableStructureAlign.RIGHT}
										/>
										{/* <RenderField
											itemWithId,
											'scheme_list',
											{ label: 'Schemes' },
											[4, 8],
											ETableStructureAlign.LEFT,
										)} */}

										{itemWithId.scheme_list && itemWithId.scheme_list.length > 0 && (
											<Grid container spacing={2} sx={{ width: '100%' }}>
												<Grid item md={2} sx={{ color: COLORS.PRIMARY }}>
													Schemes:
												</Grid>
												<Grid
													item
													md={10}
													sx={{
														display: 'flex',
														justifyContent: 'flex-end',
														gap: 0.5,
														flexDirection: 'row',
														color: COLORS.PRIMARY,
													}}
												>
													{itemWithId.scheme_list
														.filter((scheme) => scheme !== null)
														.map((scheme) => {
															const schemeColumn = tableStructure.find(
																(col) => col.selector === 'scheme_list',
															);
															const label =
																schemeColumn?.filter?.find(
																	(filterItem) => filterItem[0] === scheme,
																)?.[1] ?? scheme;
															return (
																<Chip
																	key={scheme}
																	label={label as string}
																	size="small"
																	sx={{
																		backgroundColor: COLORS.BACKGROUND_SECONDARY,
																		fontSize: '0.8rem',
																		fontWeight: 'bold',
																	}}
																/>
															);
														})}
												</Grid>
											</Grid>
										)}
										<Divider />
										{itemWithId.comment ? (
											<>
												<Box
													sx={{
														display: 'flex',
														flexDirection: 'row',
														gap: 1,
														alignItems: 'center',
														justifyContent: 'space-between',
													}}
												>
													<Typography
														sx={{ color: COLORS.PRIMARY, fontSize: '1rem', fontWeight: 'bold' }}
													>
														Comment:
													</Typography>
													<IconButton
														size="small"
														onClick={() => handleAction('Add or edit comments for the invoice')}
													>
														<Edit fontSize="small" color="primary" />
													</IconButton>
												</Box>
												<Typography
													sx={{ color: COLORS.PRIMARY, fontSize: '1rem', fontStyle: 'italic' }}
												>
													{itemWithId.comment}
												</Typography>
											</>
										) : (
											<Button
												color="primary"
												variant="contained"
												size="small"
												sx={{
													width: '150px',
												}}
												onClick={() => handleAction('Add or edit comments for the invoice')}
											>
												<Comment fontSize="small" sx={{ color: 'white' }} /> &nbsp; Add a comment
											</Button>
										)}
									</Box>
								</Grid>
							</Grid>
						</Box>
						<Divider />
						{/* Invoice breakdown */}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
							<Typography
								sx={{ color: COLORS.PRIMARY, fontSize: '1rem', fontWeight: 'bold', ml: -2 }}
							>
								Invoice breakdown
							</Typography>

							<RenderField
								item={itemWithId}
								selector="eas_fee_with_invoice_fee"
								label="EAS fees for order processing"
								hideIfZero
								value={
									itemWithId.eas_fee_with_invoice_fee
										? Number(itemWithId.eas_fee_with_invoice_fee ?? 0) -
											Number(itemWithId.monthly_fee ?? 0) -
											Number(itemWithId.non_union_plan_amount ?? 0) -
											Number(itemWithId.invoicing_fee ?? 0)
										: null
								}
							/>
							<RenderField
								item={itemWithId}
								selector="monthly_fee"
								label={`Monthly subscription (${itemWithId.monthly_fee_class_name})`}
								hideIfZero
							/>

							<RenderField
								item={itemWithId}
								selector="non_union_plan_amount"
								label={`${itemWithId.non_union_plan_name ?? 'Non-union plan'}`}
								hideIfZero
							/>
							<RenderField
								item={itemWithId}
								selector="invoicing_fee"
								label={`${itemWithId.invoicing_fee_name ?? 'Invoicing fee'}`}
								hideIfZero
							/>

							{/* {renderField(itemWithId, 'due_interest_amount', {
								label: `Late payment fee (invoice ${itemWithId.delayed_invoice_number})`,
								hideIfZero: true,
							})} */}
							{!!itemWithId.delayed_invoice_number &&
								!!itemWithId.due_interest_amount &&
								Number(itemWithId.due_interest_amount) !== 0 && (
									<Grid container spacing={2} sx={{ width: '100%' }}>
										<Grid item md={10}>
											<Typography sx={{ color: COLORS.PRIMARY, fontSize: '1rem' }} component="span">
												Late payment fee (invoice{' '}
												<Link
													to={`/invoice-management/${itemWithId.delayed_invoice_number}`}
													style={{ fontWeight: 'bold', color: COLORS.PRIMARY }}
													target="_blank"
													rel="noopener noreferrer"
												>
													{itemWithId.delayed_invoice_number}
												</Link>
												):
											</Typography>
										</Grid>
										<Grid item md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
											<EasTableValue
												type={ETableStructureType.AMOUNT}
												value={itemWithId.due_interest_amount}
												item={itemWithId}
												size="l"
												bold
											/>
										</Grid>
									</Grid>
								)}
							<RenderField
								item={itemWithId}
								selector="shipment_fee_due"
								label="EAS customs fee"
								hideIfZero
							/>

							<RenderField
								item={itemWithId}
								selector="vat_on_eas_fee_and_invoicing_fee"
								label={`VAT on EAS fees and invoicing fee (${itemWithId.vat_rate}%)`}
								hideIfZero
							/>
							<RenderField
								item={itemWithId}
								selector="non_eu_import_vat_eur"
								label={`GB VAT amount due to EAS`}
								hideIfZero
							/>
							<RenderField
								item={itemWithId}
								selector="vat_fiscal_import"
								label={`IOSS VAT due to EAS`}
								hideIfZero
							/>
							<RenderField
								item={itemWithId}
								selector="vat_fiscal_union"
								label={`UNION VAT due to EAS`}
								hideIfZero
							/>
							<RenderField
								item={itemWithId}
								selector="vat_fiscal_non_union"
								label={`NON UNION VAT due to EAS`}
								hideIfZero
							/>

							{/* <RenderField
								item={itemWithId}
								selector="vat_due_to_eas"
								label="VAT due to EAS"
								hideIfZero
							/> */}
							<RenderField
								item={itemWithId}
								selector="customs_duty_due"
								label="Customs duty"
								hideIfZero
							/>
							<RenderField
								item={itemWithId}
								selector="fc_rep_vat_eur"
								label="DDP Value Added Tax"
								hideIfZero
							/>

							<RenderField
								item={itemWithId}
								selector="invoiced_amount_in_eur"
								label="Invoiced amount"
								bold
							/>
						</Box>

						<Divider />

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
							{/* {renderField(itemWithId, 'reimbursed_amount', {
								label: `Reimbursed amount: ${itemWithId.reimbursed_countries
									?.split(',')
									?.map((country) => country.trim())
									?.map((country) => (
										<CountryWithFlag
											key={country}
											countryCode={country}
											size="xs"
											bold
											color={COLORS.PRIMARY}
										/>
									))}`,
								hideIfZero: false,
							})} */}
							<Grid container spacing={2} sx={{ width: '100%' }}>
								<Grid item md={10}>
									<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
										<Typography sx={{ color: COLORS.PRIMARY, fontSize: '1rem' }} component="span">
											Reimbursed amount
										</Typography>
										{itemWithId.reimbursed_countries &&
											itemWithId.reimbursed_countries.length > 0 && (
												<Box
													sx={{
														display: 'flex',
														flexDirection: 'row',
														gap: 1,
														alignItems: 'center',
														component: 'span',
													}}
												>
													{itemWithId.reimbursed_countries
														?.split(',')
														?.map((country) => country.trim())
														?.map((country) => (
															<CountryWithFlag
																key={country}
																countryCode={country}
																size="m"
																bold
																color={COLORS.PRIMARY}
																showLabel={false}
															/>
														))}
												</Box>
											)}
									</Box>
								</Grid>
								<Grid item md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
									<EasTableValue
										type={ETableStructureType.AMOUNT}
										value={itemWithId.reimbursed_amount}
										item={itemWithId}
										size="l"
										bold
									/>
								</Grid>
							</Grid>

							<RenderField
								item={itemWithId}
								selector="invoice_carry_over_amount"
								label="Balance from previous invoice"
							/>
							<RenderField item={itemWithId} selector="prepaid_amount" label="Prepaid amount" />

							<RenderField
								item={itemWithId}
								selector="total_invoice_amount_in_eur"
								label="Total amount"
								bold
							/>
						</Box>

						{itemWithId.vat_reimbursement_list_import &&
							itemWithId.vat_reimbursement_list_import.length > 0 && (
								<>
									<Divider />

									<Typography sx={{ color: COLORS.PRIMARY, fontSize: '1rem', fontWeight: 'bold' }}>
										Reimbursments expected
									</Typography>

									{itemWithId.vat_reimbursement_list_import.map((item) => {
										const key = Object.keys(item)[0];
										const value = item[key];
										return (
											<Grid container spacing={2} key={key} sx={{ width: '100%', pl: 2 }}>
												<Grid item md={10}>
													<CountryWithFlag
														countryCode={key}
														size="l"
														color={COLORS.PRIMARY}
														tooltip={false}
														bold
													/>
												</Grid>
												<Grid item md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
													<EasTableValue
														type={ETableStructureType.AMOUNT}
														value={value}
														item={itemWithId}
														size="l"
														bold
													/>
												</Grid>
											</Grid>
										);
									})}
								</>
							)}

						<Divider />

						{/* Payment details */}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
							<Typography
								sx={{ color: COLORS.PRIMARY, fontSize: '1rem', fontWeight: 'bold', ml: -2 }}
							>
								Payment details
							</Typography>

							<RenderField
								item={itemWithId}
								selector="date_of_payment"
								label="Payment date"
								align={ETableStructureAlign.RIGHT}
								hideIfZero
							/>
							<RenderField item={itemWithId} selector="paid_amount" label="Paid amount" />
						</Box>
						<Divider />
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
							<Typography
								sx={{ color: COLORS.PRIMARY, fontSize: '1rem', fontWeight: 'bold', ml: -2 }}
							>
								Direct debit or Online payment details
							</Typography>

							<RenderField
								item={itemWithId}
								selector="revolut_received_amount"
								label="Payment provider received (EUR)"
							/>
							<RenderField
								item={itemWithId}
								selector="revolut_fee"
								label="Payment provider fee (EUR)"
							/>
							{itemWithId.paid_thruogh && (
								<RenderField item={itemWithId} selector="paid_thruogh" label="Paid through" />
							)}
						</Box>
					</Box>
				</Paper>

				{detailRequestPayload() && (
					<>
						<InvoicePaymentDialog
							open={openPaymentDialog}
							setClose={() => setOpenPaymentDialog(false)}
							data={itemWithId}
							invoiceListRequestPayload={detailRequestPayload()!}
						/>
						<InvoiceCommentDialog
							open={openCommentDialog}
							setClose={() => setOpenCommentDialog(false)}
							data={itemWithId}
							invoiceListRequestPayload={detailRequestPayload()!}
						/>
						<ConfirmationDialog
							open={openNotificationDialog}
							setClose={() => setOpenNotificationDialog(false)}
							handleConfirm={handleNotificationConfirm}
							title="Are you sure you want to send notification to client?"
						/>
						<ConfirmationDialog
							open={openSuspendNotificationDialog}
							setClose={() => setOpenSuspendNotificationDialog(false)}
							handleConfirm={handleSuspendNotificationConfirm}
							title="Are you sure you want to suspend notifications on invoice payments?"
						/>
						<ConfirmationDialogWithComment
							open={openNullifyInvoiceDialog}
							setClose={() => setOpenNullifyInvoiceDialog(false)}
							handleConfirm={handleNullifyInvoiceConfirm}
							title="Are you sure you want to nullify invoice?"
							comment={nullifyInvoiceComment}
							setComment={setNullifyInvoiceComment}
						/>
						<ConfirmationDialogWithComment
							open={openRemoveLpfDialog}
							setClose={() => setOpenRemoveLpfDialog(false)}
							handleConfirm={handleRemoveLpfConfirm}
							title="Are you sure you want to remove LPF?"
							comment={removeLpfComment}
							setComment={setRemoveLpfComment}
						/>
					</>
				)}
			</Box>
		</Box>
	);
};
